/**
 * Экран, когда роль пользователя определить не удалось.
 *
 * Два режима, потому что причины несравнимы. Секундный сбой эджа не означает,
 * что с аккаунтом что-то не так, — предлагать в этом случае «обратитесь к
 * администратору» и кнопку выхода значит превращать блип в инцидент.
 */
import { useTranslation } from 'react-i18next';
import { SignOutButton, UserButton } from '@clerk/clerk-react';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

interface RoleErrorScreenProps {
  /** Текст ошибки для показа пользователю. */
  message: string;
  /** Повтор имеет смысл (транспортный сбой), см. OrgFetchError.retriable. */
  retriable: boolean;
  onRetry: () => void;
}

export function RoleErrorScreen({ message, retriable, onRetry }: RoleErrorScreenProps) {
  const { t } = useTranslation(['auth', 'common']);

  const buttonClass = cn(
    CONTROL_MIN_HEIGHT.md,
    'inline-flex items-center justify-center rounded-button bg-primary px-4 text-sm font-medium text-bg transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-void">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-fg mb-4">
          {retriable
            ? t('auth.connectionProblem', "Can't reach the server")
            : t('auth.unableToDetermineRole', 'Unable to determine user role')}
        </h1>
        <p className="text-fg-muted mb-4">{message}</p>
        <p className="text-sm text-fg-muted mb-6">
          {retriable
            ? t(
                'auth.connectionProblemHint',
                'This is usually temporary. Try again in a moment.'
              )
            : t(
                'auth.contactAdministrator',
                'Please contact your administrator to set up your account.'
              )}
        </p>
        <div className="flex flex-col items-center gap-3">
          {retriable && (
            <button
              type="button"
              onClick={onRetry}
              data-testid="role-error-retry"
              className={buttonClass}
            >
              {t('auth.retry', 'Try again')}
            </button>
          )}
          <SignOutButton redirectUrl="/sign-in">
            <button
              type="button"
              className={
                retriable
                  ? cn(
                      CONTROL_MIN_HEIGHT.md,
                      'inline-flex items-center justify-center rounded-button border border-border px-4 text-sm font-medium text-fg-muted transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    )
                  : buttonClass
              }
            >
              {t('auth.signOut', 'Sign out')}
            </button>
          </SignOutButton>
          <UserButton />
        </div>
      </div>
    </div>
  );
}
